import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getSavedCustomerToken, getCustomerOrders } from '@/lib/shopifyCustomer';
import { toast } from 'sonner';
import { formatDate } from '@/lib/date';

const PAYMENT_STATUS = {
  PAID: 'Pago',
  PENDING: 'Pendente',
  AUTHORIZED: 'Autorizado',
  PARTIALLY_PAID: 'Parc. pago',
  PARTIALLY_REFUNDED: 'Parc. reembolsado',
  REFUNDED: 'Reembolsado',
  VOIDED: 'Cancelado',
};

const FULFILLMENT_STATUS = {
  FULFILLED: 'Enviado',
  UNFULFILLED: 'Aguardando envio',
  PARTIALLY_FULFILLED: 'Parc. enviado',
  RESTOCKED: 'Estornado',
  IN_PROGRESS: 'Em andamento',
  ON_HOLD: 'Em espera',
  SCHEDULED: 'Agendado',
};

const translateStatus = (map, raw) => map[raw] ?? raw;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadOrders() {
      try {
        const token = getSavedCustomerToken();

        if (!token) {
          return;
        }

        const data = await getCustomerOrders(token);
        setOrders(data);
      } catch (error) {
        void error;
        toast.error('Erro ao carregar pedidos');
      } finally {
        setLoading(false);
      }
    }

    loadOrders();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-[1100px] mx-auto flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6">
      <div className="max-w-[1100px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-5xl font-bold text-text-primary mb-4">
            Meus Pedidos
          </h1>
          <p className="text-lg text-text-secondary">
            Acompanhe os pedidos feitos na Aura Rosa.
          </p>
        </motion.div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
            <p className="text-lg text-text-secondary">Você ainda não possui pedidos.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map(({ node }, idx) => (
              <motion.div
                key={node.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-2xl shadow-sm p-8"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-text-primary">
                      Pedido #{node.orderNumber}
                    </h2>
                    <p className="text-text-secondary">
                      Feito em {formatDate(node.processedAt)}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-text-secondary">
                      Pagamento: {translateStatus(PAYMENT_STATUS, node.financialStatus) || '—'}
                    </p>
                    <p className="text-text-secondary">
                      Entrega: {translateStatus(FULFILLMENT_STATUS, node.fulfillmentStatus) || '—'}
                    </p>
                    <p className="font-heading text-2xl font-bold text-accent mt-2">
                      {node.totalPrice.amount} {node.totalPrice.currencyCode}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-5 space-y-4">
                  {node.lineItems.edges.map(({ node: item }, itemIdx) => (
                    <div
                      key={itemIdx}
                      className="flex justify-between items-center gap-4"
                    >
                      <div>
                        <p className="text-text-primary font-medium">{item.title}</p>
                        <p className="text-sm text-text-secondary">
                          Quantidade: {item.quantity}
                        </p>
                      </div>

                      <p className="text-text-secondary">
                        {item.originalTotalPrice.amount} {item.originalTotalPrice.currencyCode}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;